// DeliverableTable — grouped parent/child/one-off table for the
// deliverables tab. Does NOT replace the shared TrackingTable in
// /shared/ (still used by Insurance / Milestones / KPIs).
//
// Columns (M1): Name / Owner / Due / Status / RAG / Evidence /
// Actions. The Type column is dropped — deliverable-vs-obligation
// rides next to the row name via DeliverableKindIcon, and row-kind
// (recurring / one-off / occurrence) via TypeChip.
//
// Parent rows aggregate RAG by live-worst-of-children — children
// with terminal status (accepted / rejected / closed) are historical
// records and don't inflate the parent's live signal (F4). Parent
// OccurrenceStrip reads from `allChildren` when present so a filtered
// view still shows the full schedule history.
//
// Evidence column renders the EvidenceIndicator as a focusable button
// when a row carries attachments; clicking opens a portal-rendered
// popover (EvidenceLinkPopover) anchored to the trigger via
// position: fixed coordinates so the table's overflow doesn't clip
// the popover and page scroll doesn't desync it from the anchor.
//
// Rows are keyboard-focusable with a focus-visible ring.

import { Fragment, memo, useRef, useState, type ReactNode } from "react";
import {
  Pencil,
  Archive as ArchiveIcon,
  RefreshCw,
  ChevronRight,
  Check,
} from "lucide-react";
import Badge from "../../../common/Badge";
import Button from "../../../common/Button";
import TrackingStatusBadge from "../shared/TrackingStatusBadge";
import RagIndicator from "../shared/RagIndicator";
import TypeChip from "../shared/TypeChip";
import DeliverableKindIcon from "./DeliverableKindIcon";
import DueDateCell from "./DueDateCell";
import EvidenceIndicator from "./EvidenceIndicator";
import EvidenceLinkPopover from "./EvidenceLinkPopover";
import OccurrenceStrip, { type OccurrenceTile } from "./OccurrenceStrip";
import SortIndicator from "./SortIndicator";
import DeliverableSlipTag from "./DeliverableSlipTag";
import SubmissionBadge from "./SubmissionBadge";
import FailedAcceptanceBadge from "./FailedAcceptanceBadge";
import { computeSlipVariant } from "./computeSlipVariant";
import { deriveDeliverableStatus } from "./deriveDeliverableStatus";
import { deriveNextOccurrence } from "./deriveNextOccurrence";
import { deriveSurfacing } from "./deriveSurfacing";
import { deliverableStatusTooltip } from "./deliverableStatusTooltip";
import { deriveOccurrenceTitle } from "./deriveOccurrenceTitle";
import { deriveEffectiveRag } from "./deriveEffectiveRag";
import { formatMonthYear } from "../../../../utils/formatShortDate";
import type {
  ChildRow,
  OneOffRow,
  OrphanRow,
  ParentRow,
  Row,
} from "./groupDeliverables";
import type {
  Deliverable,
  DeliverableOwner,
  DeliverableStatus,
  Milestone,
  RagRating,
} from "../../../../types";

const TERMINAL_STATUSES: ReadonlySet<DeliverableStatus> = new Set([
  "accepted",
  "rejected",
  "closed",
]);
const RAG_PRECEDENCE: RagRating[] = ["red", "amber", "green"];

// Transient highlight applied to a row whose RAG just changed via
// auto-derive. Colour matches the new RAG so the flash reinforces the
// semantic meaning. CSS transition-colors lets the highlight decay
// smoothly when the timer clears ragFlashRowId instead of cutting hard.
//
// ragFlashRagRating is the RAG from the mutation response — preferred
// over rowRagRating because the list query may not have re-fetched yet
// (the row in the table could still carry stale or undefined RAG data).
function ragFlashClass(
  deliverableId: string,
  ragFlashRowId: string | null | undefined,
  rowRagRating: RagRating | undefined,
  ragFlashRagRating?: RagRating | null,
): string {
  if (!ragFlashRowId || ragFlashRowId !== deliverableId) return "";
  const rag = ragFlashRagRating ?? rowRagRating;
  if (rag === "green") {
    return "bg-emerald-50 dark:bg-emerald-900/20 transition-colors duration-700";
  }
  if (rag === "amber") {
    return "bg-amber-50 dark:bg-amber-900/20 transition-colors duration-700";
  }
  if (rag === "red") {
    return "bg-red-50 dark:bg-red-900/20 transition-colors duration-700";
  }
  return "";
}

// Worst-of-live-children aggregation. Terminal children are historical
// outcomes and must not inflate the parent's live signal. Among live
// children, compare EFFECTIVE RAG so an overdue child with no stored
// RAG still raises the parent aggregate to red (F4).
function parentAggregateRag(children: ChildRow[]): RagRating | null {
  const live = new Set<RagRating>();
  for (const c of children) {
    if (TERMINAL_STATUSES.has(c.deliverable.status)) continue;
    const effective = deriveEffectiveRag(c.deliverable);
    if (effective) live.add(effective);
  }
  for (const r of RAG_PRECEDENCE) {
    if (live.has(r)) return r;
  }
  return null;
}

export type DeliverableAction =
  | "primary"
  | "edit"
  | "archive"
  | "reject";

export type DeliverableSortColumn =
  | "name"
  | "owner"
  | "due"
  | "status"
  | "rag";
export type DeliverableSortDirection = "ascending" | "descending" | null;

export interface DeliverableTableProps {
  rows: Row[];
  expandedScheduleIds: Set<string>;
  onToggleExpand: (parentId: string) => void;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  highlightRowId?: string | null;
  /** Deliverable id whose row should display a transient RAG-change
   *  flash highlight. Cleared by the parent after the auto-dismiss
   *  timer fires. */
  ragFlashRowId?: string | null;
  /** RAG rating from the mutation response — used for the flash colour
   *  because the list query may not have re-fetched by render time. */
  ragFlashRagRating?: RagRating | null;
  /**
   * Milestone lookup keyed by id. Used to render the milestone name
   * as a muted sub-line under the deliverable name. Missing keys
   * render nothing — a deleted/archived milestone must not surface
   * "unknown milestone" text.
   */
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  /** Optional sort wiring — when supplied, the sortable column
   *  headers render as SortIndicators with aria-sort, click-to-cycle
   *  semantics, and brand-coloured carets. When omitted, headers
   *  render as plain <th>s. */
  sortColumn?: DeliverableSortColumn | null;
  sortDirection?: DeliverableSortDirection;
  onSortChange?: (next: {
    column: DeliverableSortColumn | null;
    direction: DeliverableSortDirection;
  }) => void;
  /** Deliverable id currently shown in the inline detail expand. When
   *  set, an extra `<tr>` is injected directly after that row so the
   *  panel stays attached to the clicked occurrence rather than
   *  floating below a long recurrence block. */
  expandedItemId?: string | null;
  /** Renderer for the inline detail panel (read-only or edit form).
   *  Receives the deliverable for the row; the table wraps its return
   *  in a `<tr><td colspan>` that spans every column. */
  renderExpandedRow?: (deliverable: Deliverable) => ReactNode;
}

const COLUMN_COUNT = 7;

const OWNER_LABELS: Record<DeliverableOwner, string> = {
  supplier: "Supplier",
  customer: "Customer",
  joint: "Joint",
};

// Shared focus-ring classes applied to every keyboard-navigable <tr>
// so users have a visible indicator of the active row. Inset rings
// avoid clipping adjacent cells.
const FOCUS_RING =
  "focus:outline-hidden focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-inset";

// Row-level mute for archived rows. Muting only the status badge and
// due cell leaves the rest of the row (name, type chip, owner,
// evidence) at full opacity, which makes busy archived sections hard
// to scan past. Drop the whole row's text colour + opacity so the row
// reads as a single historical block.
const ARCHIVED_ROW_MUTE = "opacity-60 text-slate-400 dark:text-slate-500";

// Darker neutral fill for the row whose inline detail panel is open, so it
// reads as the obvious source of the popout below. Matches the shared
// TrackingTable selected-row treatment for cross-type consistency.
const SELECTED_ROW_CLASS = "bg-slate-100 dark:bg-slate-700";

// Closed (status='closed') deliverables aren't archived but they are
// done — the user shouldn't be drawn to them while scanning live work.
// Italic + reduced opacity on the name reads as "completed, kept for
// the record" without making the whole row disappear like archived
// does.
const CLOSED_ROW_NAME = "italic opacity-70";

function nameClass(d: Deliverable): string {
  const base = "text-slate-900 dark:text-slate-100";
  return d.status === "closed" ? `${base} ${CLOSED_ROW_NAME}` : base;
}

function toOccurrenceTile(c: ChildRow, now: Date): OccurrenceTile {
  return {
    id: c.deliverable.id,
    status: deriveDeliverableStatus(
      c.deliverable.status,
      c.deliverable.dueDate,
      now,
      c.deliverable.submittedAt,
    ),
    rag: deriveEffectiveRag(c.deliverable),
    period: deriveOccurrenceTitle(c.deliverable, c.parent),
  };
}

function frequencyLabel(freq: string): string {
  switch (freq) {
    case "daily":
      return "Daily";
    case "weekly":
      return "Weekly";
    case "monthly":
      return "Monthly";
    case "quarterly":
      return "Quarterly";
    case "annually":
      return "Annually";
    default:
      return freq;
  }
}

function formatMonthYearIso(iso: string | undefined): string {
  return iso ? formatMonthYear(iso) : "";
}

function parentSubline(parent: Deliverable): string {
  const rule = parent.recurrenceRule;
  if (!rule) return "";
  const parts = [frequencyLabel(rule.frequency), formatMonthYearIso(rule.startDate)];
  if (rule.endDate) parts.push(`– ${formatMonthYearIso(rule.endDate)}`);
  return parts.filter(Boolean).join(" · ");
}

/** Surfaces a "last: Amber (Apr 2026)" annotation on the parent
 *  sub-line when the most-recently-completed terminal child carries
 *  Amber or Red. Green is filtered out — a green last completion is
 *  not a heads-up. Returns "" when no annotation applies. */
function lastCompletedRagAnnotation(
  children: readonly { deliverable: Deliverable }[],
): string {
  // Pick the latest terminal (accepted / closed) child by acceptedAt
  // (falls back to updatedAt so old fixtures without acceptedAt still
  // sort meaningfully).
  let latest: Deliverable | null = null;
  let latestKey = "";
  for (const c of children) {
    const d = c.deliverable;
    if (d.status !== "accepted" && d.status !== "closed") continue;
    const key = d.acceptedAt ?? d.updatedAt ?? "";
    if (!latest || key > latestKey) {
      latest = d;
      latestKey = key;
    }
  }
  if (!latest) return "";
  const rag = latest.ragRating;
  if (rag !== "amber" && rag !== "red") return "";
  const periodSource = latest.dueDate ?? latest.occurrenceDate;
  const periodText = formatMonthYearIso(periodSource);
  const ragText = rag === "amber" ? "Amber" : "Red";
  return periodText
    ? `last: ${ragText} (${periodText})`
    : `last: ${ragText}`;
}

function MilestoneSubline({
  milestoneId,
  milestonesById,
}: {
  milestoneId?: string;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
}) {
  if (!milestoneId || !milestonesById) return null;
  const milestone = milestonesById[milestoneId];
  if (!milestone) return null;
  return (
    <span className="block text-caption text-slate-500 dark:text-slate-400 truncate">
      {milestone.name}
    </span>
  );
}

// Compact row of meta pills displayed under the name. Surfaces the slip
// cue (red / amber) when the deliverable is past due or was submitted
// late, a SubmissionBadge when the deliverable has been re-submitted
// (count > 1), and a FailedAcceptanceBadge when failedAcceptanceCount
// > 0. Each cue renders only when it carries signal, so the row is
// empty by default and adds visible weight only when there's something
// the user needs to see.
function DeliverableMetaLine({
  deliverable,
  now,
}: {
  deliverable: Deliverable;
  now: Date;
}) {
  const slip = computeSlipVariant(
    {
      dueDate: deliverable.dueDate,
      submittedAt: deliverable.submittedAt,
    },
    now,
  );
  const showSlip = slip.variant !== "none";
  const showSubmission = (deliverable.submissionCount ?? 0) > 1;
  const failedCount = deliverable.failedAcceptanceCount ?? 0;
  const showFailed = failedCount > 0;
  if (!showSlip && !showSubmission && !showFailed) return null;
  return (
    <span className="mt-0.5 inline-flex flex-wrap items-center gap-1.5">
      {showSlip && (
        <DeliverableSlipTag
          variant={slip.variant as "late" | "submitted_late"}
          days={slip.days}
          dueDate={deliverable.dueDate}
          submittedAt={deliverable.submittedAt}
        />
      )}
      {showSubmission && (
        <SubmissionBadge count={deliverable.submissionCount ?? 0} />
      )}
      {showFailed && <FailedAcceptanceBadge count={failedCount} />}
    </span>
  );
}

// Anchor for the evidence-link popover. Owns its own open/closed
// state + the trigger's bounding rect so the portal-rendered popover
// positions correctly. The trigger button stops
// click propagation so opening the popover doesn't toggle the
// row's view-mode expansion.
function EvidenceCell({ deliverable }: { deliverable: Deliverable }) {
  const links = deliverable.evidenceLinks ?? [];
  const [open, setOpen] = useState(false);
  const [anchorRect, setAnchorRect] = useState<DOMRect | null>(null);
  const buttonRef = useRef<HTMLSpanElement>(null);
  const handleOpen = links.length > 0
    ? () => {
        const rect = buttonRef.current?.getBoundingClientRect();
        if (rect) setAnchorRect(rect);
        setOpen((o) => !o);
      }
    : undefined;
  return (
    <td className="px-3 py-2" onClick={(e) => e.stopPropagation()}>
      <span ref={buttonRef} className="inline-block">
        <EvidenceIndicator count={links.length} onClick={handleOpen} />
      </span>
      {open && anchorRect && (
        <EvidenceLinkPopover
          rowId={deliverable.id}
          links={links}
          anchorRect={anchorRect}
          onClose={() => setOpen(false)}
        />
      )}
    </td>
  );
}

// Archived rows render an "Archived" badge instead of the derived
// live status (Overdue / Submitted / etc) — those rows aren't live
// anymore and the live label misleads. Falls through to
// the regular TrackingStatusBadge for non-archived rows.
function StatusCell({ deliverable, now }: { deliverable: Deliverable; now: Date }) {
  if (deliverable.archivedAt) {
    return (
      <td className="px-3 py-2">
        <Badge variant="archived" label="Archived" />
      </td>
    );
  }
  const derivedStatus = deriveDeliverableStatus(
    deliverable.status,
    deliverable.dueDate,
    now,
    deliverable.submittedAt,
  );
  // Hover tooltip carries the underlying timestamp + status context
  // (e.g. "Submitted 30 Mar 2026 · awaiting customer review"). When
  // the relevant timestamp is missing, deliverableStatusTooltip
  // returns undefined and we skip the wrapper entirely so the DOM
  // doesn't carry an empty `title` attribute.
  const tooltip = deliverableStatusTooltip(deliverable);
  const badge = (
    <TrackingStatusBadge kind="deliverable" status={derivedStatus} />
  );
  return (
    <td className="px-3 py-2">
      {tooltip !== undefined ? (
        <span
          data-testid={`status-tooltip-${deliverable.id}`}
          title={tooltip}
        >
          {badge}
        </span>
      ) : (
        badge
      )}
    </td>
  );
}

// Actions cell wrapper carries a stable testid + a width-controlling
// class so the column doesn't visually shrink when a row's context
// primary is hidden.
const ACTIONS_CELL_CLASS = "px-3 py-2 text-right";

function ActionsCell({
  row,
  onAction,
  deliverable,
  now,
}: {
  row: Row;
  onAction: (row: Row, action: DeliverableAction) => void;
  deliverable: Deliverable;
  now: Date;
}) {
  return (
    <td
      data-testid={`actions-cell-${deliverable.id}`}
      className={ACTIONS_CELL_CLASS}
    >
      <ActionButtons row={row} onAction={onAction} deliverable={deliverable} now={now} />
    </td>
  );
}

interface ActionButtonsProps {
  row: Row;
  onAction: (row: Row, action: DeliverableAction) => void;
  deliverable: Deliverable;
  now: Date;
  /** Parent rows whose surfacing phase is `completed` hide the
   *  refresh / generate-occurrences action — there's nothing left
   *  to generate. */
  hideRefresh?: boolean;
}

// Child + one-off rows surface a context-sensitive primary action
// driven by the derived status. Parent rows keep the existing
// Generate-occurrences slot. Statuses without a meaningful
// transition (under-review / revision-requested / rejected /
// in-progress / accepted / closed) hide the slot — the actions cell
// width stays constant via a wrapper so icons align across rows.
type ContextPrimary = {
  icon: React.ReactNode;
  label: string;
} | null;

function contextPrimaryFor(
  deliverable: Deliverable,
  displayName: string,
  now: Date,
): ContextPrimary {
  const derived = deriveDeliverableStatus(
    deliverable.status,
    deliverable.dueDate,
    now,
    deliverable.submittedAt,
  );
  if (derived === "not-started" || derived === "overdue") {
    return {
      icon: <Check className="h-4 w-4" />,
      label: `Mark submitted for ${displayName}`,
    };
  }
  // Gate-applicable rows: open the acceptance gate dialog for
  // customer-review decisions (accept / conditionally-accept / fail /
  // reject) and supplier-resubmission (from awaiting-resubmission).
  // `late` is the submitted-past-due variant of the customer-review
  // queue and shares the same gate outcomes as `submitted`. Button
  // label is kept as "Approve" (legacy copy) — the dialog itself
  // surfaces the full outcome set.
  if (
    derived === "submitted" ||
    derived === "under-review" ||
    derived === "late" ||
    derived === "conditionally-accepted" ||
    derived === "awaiting-resubmission"
  ) {
    return {
      icon: <Check className="h-4 w-4" />,
      label: `Approve ${displayName}`,
    };
  }
  return null;
}

/** Compose the human-readable name for action labels. Child rows append
 *  the occurrence period (e.g. "Monthly Service Report — Apr 2026") so
 *  screen-reader users can disambiguate actions across a recurring
 *  schedule's occurrences. One-off, orphan, and parent rows use the
 *  deliverable's own name unchanged. */
function actionLabelName(row: Row, deliverable: Deliverable): string {
  if (row.kind === "child") {
    const period = deriveOccurrenceTitle(row.deliverable, row.parent);
    if (period && period !== row.parent.name) {
      return `${row.parent.name} — ${period}`;
    }
  }
  return deliverable.name;
}

function ActionButtons({
  row,
  onAction,
  deliverable,
  now,
  hideRefresh = false,
}: ActionButtonsProps) {
  const isParent = row.kind === "parent";
  const displayName = actionLabelName(row, deliverable);
  const contextPrimary = !isParent
    ? contextPrimaryFor(deliverable, displayName, now)
    : null;
  // Reject for gate-applicable rows is an outcome inside the
  // AcceptanceGateDialog (opened via the primary button), so the
  // dedicated reject row-button is no longer rendered. Routing matrix:
  // not-started/overdue → MarkPopover; submitted/under-review/late/
  // conditionally-accepted/awaiting-resubmission → AcceptanceGate.
  return (
    <div className="inline-flex gap-1">
      {isParent && !hideRefresh && (
        <Button
          variant="ghost"
          iconOnly
          size="sm"
          icon={<RefreshCw className="h-4 w-4" />}
          aria-label={`Generate occurrences for ${displayName}`}
          title={`Generate occurrences for ${displayName}`}
          data-testid={`action-primary-${deliverable.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onAction(row, "primary");
          }}
        />
      )}
      {contextPrimary && (
        <Button
          variant="ghost"
          iconOnly
          size="sm"
          icon={contextPrimary.icon}
          aria-label={contextPrimary.label}
          title={contextPrimary.label}
          data-testid={`action-primary-${deliverable.id}`}
          onClick={(e) => {
            e.stopPropagation();
            onAction(row, "primary");
          }}
        />
      )}
      <Button
        variant="ghost"
        iconOnly
        size="sm"
        icon={<Pencil className="h-4 w-4" />}
        aria-label={`Edit ${displayName}`}
        title={`Edit ${displayName}`}
        data-testid={`action-edit-${deliverable.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onAction(row, "edit");
        }}
      />
      <Button
        variant="ghost"
        iconOnly
        size="sm"
        icon={<ArchiveIcon className="h-4 w-4" />}
        aria-label={`Archive ${displayName}`}
        title={`Archive ${displayName}`}
        data-testid={`action-archive-${deliverable.id}`}
        onClick={(e) => {
          e.stopPropagation();
          onAction(row, "archive");
        }}
      />
    </div>
  );
}

interface ParentRowRenderProps {
  row: ParentRow;
  expanded: boolean;
  highlighted: boolean;
  onToggleExpand: (parentId: string) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  now: Date;
}

const ParentRowRender = memo(function ParentRowRender({
  row,
  expanded,
  highlighted,
  onToggleExpand,
  onAction,
  now,
}: ParentRowRenderProps) {
  const parent = row.deliverable;
  const stripChildren = row.allChildren ?? row.children;
  const aggregatedRag = parentAggregateRag(row.children);
  const firstChildId = row.children[0]?.deliverable.id;
  // Surfacing operates on `row.children` — the BUCKET-LOCAL subset —
  // so each distributed parent instance shows state relevant to ITS
  // bucket. Schedule-wide context (the occurrence strip) keeps using
  // `allChildren` separately. Without this split, the same
  // "Next: 15 Apr · submitted" label appears in every bucket the
  // parent touches, masking the per-bucket meaning.
  const surfacing = deriveSurfacing(row, now);
  const surfacedChild =
    surfacing.surfacedOccurrenceId !== null
      ? stripChildren.find(
          (c) => c.deliverable.id === surfacing.surfacedOccurrenceId,
        )?.deliverable ?? null
      : null;
  // Empty schedules (totalOccurrences === 0) keep the legacy "—"
  // rendering AND the refresh button — there are no occurrences yet,
  // so "All N complete" is semantically wrong and the user needs the
  // Generate Occurrences action. Only schedules with real children
  // route through the surfacing-aware DueDateCell.
  const hasChildren = surfacing.totalOccurrences > 0;
  // P4 v3 §6.2: when the chip-filter narrows row.children to a subset
  // that excludes the surfaced occurrence (surfacing is computed from
  // allChildren), the "Next:" copy on the parent points at a row the
  // user can't currently see. Show a small hint so the disconnect is
  // not silent. Hint suppresses when surfacedId is null (e.g. completed
  // schedule), when the filter hasn't narrowed (allChildren absent),
  // or when the surfaced child is in the visible children set.
  const isCompleted = hasChildren && surfacing.phase === "completed";
  const isUrgent =
    surfacing.phase === "active" && surfacing.surfacingReason === "overdue";
  const dueMode: import("./DueDateCell").DueDateCellMode =
    surfacing.phase === "completed"
      ? "parent-completed"
      : surfacing.phase === "awaiting_first"
        ? "parent-awaiting"
        : isUrgent
          ? "parent-urgent"
          : "parent-active-future";
  // Use allChildren when available so a summary filter doesn't hide
  // occurrences from the next-due derivation. nextOccurrence is kept
  // for the status-badge fallback path below.
  const nextOccurrence = deriveNextOccurrence(row.allChildren ?? row.children);
  const borderClass = expanded
    ? "border-l-2 border-brand-600 dark:border-brand-400"
    : "border-l-2 border-transparent";
  return (
    <tr
      data-testid={`deliverable-row-parent-${parent.id}`}
      tabIndex={0}
      role="row"
      aria-expanded={expanded}
      aria-controls={firstChildId ? `deliverable-row-child-${firstChildId}` : undefined}
      onClick={() => onToggleExpand(parent.id)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onToggleExpand(parent.id);
        }
      }}
      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${borderClass} ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      }`}
    >
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <span
            data-testid={`parent-chevron-${parent.id}`}
            className={`mt-0.5 inline-flex shrink-0 transition-transform ${
              expanded ? "rotate-90" : ""
            }`}
          >
            <ChevronRight className="h-4 w-4 text-slate-500 dark:text-slate-400" />
          </span>
          <div className="flex flex-col">
            <span className="inline-flex items-center gap-2">
              <DeliverableKindIcon deliverableType={parent.deliverableType} />
              <span className="font-medium text-slate-900 dark:text-slate-100">
                {parent.name}
              </span>
              <TypeChip type="recurring" />
            </span>
            <span
              data-testid={`parent-subline-${parent.id}`}
              className="text-caption text-slate-500 dark:text-slate-400"
            >
              {(() => {
                const base = parentSubline(parent);
                const annotation = lastCompletedRagAnnotation(stripChildren);
                if (!annotation) return base;
                return base ? `${base} · ${annotation}` : annotation;
              })()}
            </span>
            <div className="mt-1">
              <OccurrenceStrip
                occurrences={stripChildren.map((c) => toOccurrenceTile(c, now))}
                surfacedId={surfacing.surfacedOccurrenceId ?? undefined}
                urgent={isUrgent}
              />
            </div>
          </div>
        </div>
      </td>
      <td className="px-3 py-2 align-top text-slate-700 dark:text-slate-300">
        {OWNER_LABELS[parent.owner]}
      </td>
      <td
        data-testid={`parent-due-cell-${parent.id}`}
        className="px-3 py-2 align-top"
      >
        {hasChildren ? (
          <DueDateCell
            mode={dueMode}
            dueDate={surfacedChild?.dueDate}
            status={surfacedChild?.status ?? parent.status}
            submittedAt={surfacedChild?.submittedAt}
            totalOccurrences={surfacing.totalOccurrences}
          />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <td
        data-testid={`parent-status-cell-${parent.id}`}
        className="px-3 py-2 align-top"
      >
        {nextOccurrence ? (
          <span className="italic">
            <TrackingStatusBadge
              kind="deliverable"
              status={deriveDeliverableStatus(
                nextOccurrence.status,
                nextOccurrence.dueDate,
                undefined,
                nextOccurrence.submittedAt,
              )}
            />
          </span>
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <td
        data-testid={`parent-rag-cell-${parent.id}`}
        className="px-3 py-2 align-top"
      >
        {aggregatedRag ? (
          <RagIndicator rating={aggregatedRag} />
        ) : (
          // Empty cell when no RAG is set — matches the milestone tab's
          // RAG-cell-on-no-rating treatment. Plain "Not set" text read
          // louder than necessary on otherwise quiet rows.
          <span aria-hidden="true" />
        )}
      </td>
      <td
        data-testid={`parent-evidence-cell-${parent.id}`}
        className="px-3 py-2 align-top text-slate-400 dark:text-slate-500"
      >
        —
      </td>
      <td className="px-3 py-2 align-top text-right">
        <ActionButtons
          row={row}
          onAction={onAction}
          deliverable={parent}
          now={now}
          hideRefresh={isCompleted}
        />
      </td>
    </tr>
  );
});

interface ChildRowRenderProps {
  row: ChildRow;
  highlighted: boolean;
  ragFlashRowId?: string | null;
  ragFlashRagRating?: RagRating | null;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  now: Date;
  /** P4 surfacing — when set, this row matches the parent's
   *  surfacing.surfacedOccurrenceId. 'overdue' applies the urgent
   *  red NEXT badge + red wash; 'normal' applies the brand-violet
   *  pair. null means no surfacing markup. */
  surfacedAsReason?: "normal" | "overdue" | null;
  /** True when this row's inline detail panel is open — applies the
   *  darker selected-row fill. */
  selected?: boolean;
}

const ChildRowRender = memo(function ChildRowRender({
  row,
  highlighted,
  ragFlashRowId,
  ragFlashRagRating,
  onRowClick,
  onAction,
  milestonesById,
  now,
  surfacedAsReason = null,
  selected = false,
}: ChildRowRenderProps) {
  const d = row.deliverable;
  const title = deriveOccurrenceTitle(d, row.parent);
  const effectiveRag = deriveEffectiveRag(d);
  // Soft left-edge wash so the surfaced child is recognisable at a
  // glance even when scanning the expanded children. Red for urgent
  // surfacing (overdue), brand-violet for next-due / awaiting-first.
  const washClass =
    surfacedAsReason === "overdue"
      ? "bg-linear-to-r from-red-50/60 to-transparent dark:from-red-900/20"
      : surfacedAsReason === "normal"
        ? "bg-linear-to-r from-brand-50/60 to-transparent dark:from-brand-900/20"
        : "";
  const flashClass = ragFlashClass(d.id, ragFlashRowId, d.ragRating, ragFlashRagRating);
  return (
    <tr
      id={`deliverable-row-child-${d.id}`}
      data-testid={`deliverable-row-child-${d.id}`}
      tabIndex={0}
      role="row"
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
      className={`cursor-pointer border-l-2 border-brand-400/25 dark:border-brand-400/30 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      } ${selected ? SELECTED_ROW_CLASS : ""} ${d.archivedAt ? ARCHIVED_ROW_MUTE : ""} ${washClass} ${flashClass}`}
    >
      <td className="pl-8 pr-3 py-2">
        <div className="flex flex-col">
          <span className="inline-flex items-center gap-2">
            <DeliverableKindIcon deliverableType={d.deliverableType} />
            <span className={nameClass(d)}>{title}</span>
            <TypeChip type="occurrence" />
            {surfacedAsReason !== null && (
              <span
                data-testid="next-badge"
                className={
                  surfacedAsReason === "overdue"
                    ? "inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-red-700 dark:bg-red-900/30 dark:text-red-300"
                    : "inline-flex items-center rounded-full bg-brand-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-brand-700 dark:bg-brand-900/30 dark:text-brand-300"
                }
              >
                NEXT
              </span>
            )}
          </span>
          <MilestoneSubline
            milestoneId={d.milestoneId}
            milestonesById={milestonesById}
          />
          <DeliverableMetaLine deliverable={d} now={now} />
        </div>
      </td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
        {OWNER_LABELS[d.owner]}
      </td>
      <td className="px-3 py-2">
        <DueDateCell
          dueDate={d.dueDate}
          status={d.status}
          archived={!!d.archivedAt}
        />
      </td>
      <StatusCell deliverable={d} now={now} />
      <td data-testid={`row-rag-cell-${d.id}`} className="px-3 py-2">
        {effectiveRag ? (
          <RagIndicator rating={effectiveRag} />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <EvidenceCell deliverable={d} />
      <ActionsCell row={row} onAction={onAction} deliverable={d} now={now} />
    </tr>
  );
});

interface OneOffRowRenderProps {
  row: OneOffRow;
  highlighted: boolean;
  ragFlashRowId?: string | null;
  ragFlashRagRating?: RagRating | null;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  now: Date;
  /** True when this row's inline detail panel is open. */
  selected?: boolean;
}

const OneOffRowRender = memo(function OneOffRowRender({
  row,
  highlighted,
  ragFlashRowId,
  ragFlashRagRating,
  onRowClick,
  onAction,
  milestonesById,
  now,
  selected = false,
}: OneOffRowRenderProps) {
  const d = row.deliverable;
  const effectiveRag = deriveEffectiveRag(d);
  const flashClass = ragFlashClass(d.id, ragFlashRowId, d.ragRating, ragFlashRagRating);
  return (
    <tr
      data-testid={`deliverable-row-oneoff-${d.id}`}
      tabIndex={0}
      role="row"
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      } ${selected ? SELECTED_ROW_CLASS : ""} ${d.archivedAt ? ARCHIVED_ROW_MUTE : ""} ${flashClass}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <span
            data-testid={`chevron-placeholder-${d.id}`}
            className="invisible mt-0.5 inline-flex shrink-0"
            aria-hidden="true"
          >
            <ChevronRight className="h-4 w-4" />
          </span>
          <div className="flex flex-col">
            <span className="inline-flex items-center gap-2">
              <DeliverableKindIcon deliverableType={d.deliverableType} />
              <span className={nameClass(d)}>{d.name}</span>
              <TypeChip type="one-off" />
            </span>
            <MilestoneSubline
              milestoneId={d.milestoneId}
              milestonesById={milestonesById}
            />
            <DeliverableMetaLine deliverable={d} now={now} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
        {OWNER_LABELS[d.owner]}
      </td>
      <td className="px-3 py-2">
        <DueDateCell
          dueDate={d.dueDate}
          status={d.status}
          archived={!!d.archivedAt}
        />
      </td>
      <StatusCell deliverable={d} now={now} />
      <td data-testid={`row-rag-cell-${d.id}`} className="px-3 py-2">
        {effectiveRag ? (
          <RagIndicator rating={effectiveRag} />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <EvidenceCell deliverable={d} />
      <ActionsCell row={row} onAction={onAction} deliverable={d} now={now} />
    </tr>
  );
});

interface OrphanRowRenderProps {
  row: OrphanRow;
  highlighted: boolean;
  ragFlashRowId?: string | null;
  ragFlashRagRating?: RagRating | null;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  now: Date;
  /** True when this row's inline detail panel is open. */
  selected?: boolean;
}

// OrphanRow: orphan occurrence (parent schedule missing). Visually
// matches a one-off row's column rhythm but carries an Orphan
// TypeChip so the data inconsistency is loud at the row level (the
// tab also mounts a banner above the table summarising the count).
const OrphanRowRender = memo(function OrphanRowRender({
  row,
  highlighted,
  ragFlashRowId,
  ragFlashRagRating,
  onRowClick,
  onAction,
  milestonesById,
  now,
  selected = false,
}: OrphanRowRenderProps) {
  const d = row.deliverable;
  const effectiveRag = deriveEffectiveRag(d);
  const flashClass = ragFlashClass(d.id, ragFlashRowId, d.ragRating, ragFlashRagRating);
  return (
    <tr
      data-testid={`deliverable-row-orphan-${d.id}`}
      tabIndex={0}
      role="row"
      onClick={() => onRowClick(row)}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onRowClick(row);
        }
      }}
      className={`cursor-pointer hover:bg-slate-50 dark:hover:bg-slate-700/50 transition-colors ${FOCUS_RING} ${
        highlighted ? "ring-2 ring-brand-400" : ""
      } ${selected ? SELECTED_ROW_CLASS : ""} ${d.archivedAt ? ARCHIVED_ROW_MUTE : ""} ${flashClass}`}
    >
      <td className="px-3 py-2">
        <div className="flex items-start gap-2">
          <span
            data-testid={`chevron-placeholder-${d.id}`}
            className="invisible mt-0.5 inline-flex shrink-0"
            aria-hidden="true"
          >
            <ChevronRight className="h-4 w-4" />
          </span>
          <div className="flex flex-col">
            <span className="inline-flex items-center gap-2">
              <DeliverableKindIcon deliverableType={d.deliverableType} />
              <span className={nameClass(d)}>{d.name}</span>
              <TypeChip type="orphan" />
            </span>
            <MilestoneSubline
              milestoneId={d.milestoneId}
              milestonesById={milestonesById}
            />
            <DeliverableMetaLine deliverable={d} now={now} />
          </div>
        </div>
      </td>
      <td className="px-3 py-2 text-slate-700 dark:text-slate-300">
        {OWNER_LABELS[d.owner]}
      </td>
      <td className="px-3 py-2">
        <DueDateCell
          dueDate={d.dueDate}
          status={d.status}
          archived={!!d.archivedAt}
        />
      </td>
      <StatusCell deliverable={d} now={now} />
      <td data-testid={`row-rag-cell-${d.id}`} className="px-3 py-2">
        {effectiveRag ? (
          <RagIndicator rating={effectiveRag} />
        ) : (
          <span className="text-slate-400 dark:text-slate-500">—</span>
        )}
      </td>
      <EvidenceCell deliverable={d} />
      <ActionsCell row={row} onAction={onAction} deliverable={d} now={now} />
    </tr>
  );
});

function renderHeader(
  column: DeliverableSortColumn,
  label: string,
  sortColumn: DeliverableSortColumn | null | undefined,
  sortDirection: DeliverableSortDirection | undefined,
  onSortChange:
    | ((next: {
        column: DeliverableSortColumn | null;
        direction: DeliverableSortDirection;
      }) => void)
    | undefined,
) {
  if (!onSortChange) {
    return (
      <th
        key={column}
        className="px-3 py-2 font-medium bg-slate-50 dark:bg-slate-800"
      >
        {label}
      </th>
    );
  }
  return (
    <SortIndicator
      key={column}
      column={column}
      label={label}
      activeColumn={sortColumn ?? null}
      direction={sortDirection ?? null}
      onSortChange={(next) =>
        onSortChange({
          column: next.column as DeliverableSortColumn | null,
          direction: next.direction as DeliverableSortDirection,
        })
      }
      thClassName="px-3 py-2 font-medium bg-slate-50 dark:bg-slate-800"
    />
  );
}

/** Shared column count for header + colSpan'd rows. Exported so the
 *  grouped view can render a colSpan'd heading row that spans the same
 *  columns. */
export const DELIVERABLE_TABLE_COLUMN_COUNT = COLUMN_COUNT;

/** Inner <tr> for the deliverable table header. Rendered inside a
 *  <thead> by the standalone DeliverableTable; the grouped view
 *  reuses it so column widths line up across both layouts. */
export interface DeliverableTableHeadRowProps {
  sortColumn?: DeliverableSortColumn | null;
  sortDirection?: DeliverableSortDirection;
  onSortChange?: (next: {
    column: DeliverableSortColumn | null;
    direction: DeliverableSortDirection;
  }) => void;
}

export function DeliverableTableHeadRow({
  sortColumn,
  sortDirection,
  onSortChange,
}: DeliverableTableHeadRowProps) {
  return (
    <tr className="text-left text-caption uppercase tracking-wide text-slate-500 dark:text-slate-400">
      {renderHeader("name", "Name", sortColumn, sortDirection, onSortChange)}
      {renderHeader("owner", "Owner", sortColumn, sortDirection, onSortChange)}
      {renderHeader("due", "Due", sortColumn, sortDirection, onSortChange)}
      {renderHeader(
        "status",
        "Status",
        sortColumn,
        sortDirection,
        onSortChange,
      )}
      {renderHeader("rag", "RAG", sortColumn, sortDirection, onSortChange)}
      <th className="px-3 py-2 font-medium bg-slate-50 dark:bg-slate-800">
        Evidence
      </th>
      <th className="px-3 py-2 font-medium text-right bg-slate-50 dark:bg-slate-800">
        Actions
      </th>
    </tr>
  );
}

export interface DeliverableTableBodyRowsProps {
  rows: Row[];
  expandedScheduleIds: Set<string>;
  onToggleExpand: (parentId: string) => void;
  onRowClick: (row: ChildRow | OneOffRow | OrphanRow) => void;
  onAction: (row: Row, action: DeliverableAction) => void;
  highlightRowId?: string | null;
  ragFlashRowId?: string | null;
  ragFlashRagRating?: RagRating | null;
  milestonesById?: Record<string, Pick<Milestone, "id" | "name">>;
  expandedItemId?: string | null;
  renderExpandedRow?: (deliverable: Deliverable) => ReactNode;
}

/** Renders the row tree (parents/children/one-offs/orphans + inline
 *  expand) without a <tbody> wrapper. Callers supply their own
 *  <tbody> so the grouped view can attach id + aria-controls per
 *  group. */
export function DeliverableTableBodyRows({
  rows,
  expandedScheduleIds,
  onToggleExpand,
  onRowClick,
  onAction,
  highlightRowId,
  ragFlashRowId,
  ragFlashRagRating,
  milestonesById,
  expandedItemId,
  renderExpandedRow,
}: DeliverableTableBodyRowsProps) {
  // Single Date instantiation per render — shared across all row-level
  // helpers (status derivation, slip variant, surfacing) so a single
  // render doesn't produce a spread of Date values over its lifetime.
  const now = new Date();

  const inlineExpand = (deliverable: Deliverable): ReactNode => {
    if (!renderExpandedRow || expandedItemId !== deliverable.id) return null;
    return (
      <tr data-testid={`deliverable-inline-expand-${deliverable.id}`}>
        {/* Quiet neutral band + thin brand connector, matching the shared
         *  TrackingTable expanded cell. The white detail card comes from
         *  TrackingDetailPanel itself, so we render the detail directly here
         *  — no extra wrapper card (that produced a double border). */}
        <td
          colSpan={COLUMN_COUNT}
          className="bg-slate-50 dark:bg-slate-800/40 border-l-2 border-brand-500 rounded-r-md p-3"
        >
          {renderExpandedRow(deliverable)}
        </td>
      </tr>
    );
  };
  return (
    <>
      {rows.map((row) => {
        if (row.kind === "parent") {
          const expanded = expandedScheduleIds.has(row.deliverable.id);
          // P4 surfacing — compute once per expanded parent so the
          // surfaced child can render its NEXT badge + wash. Mirrors
          // ParentRowRender's surfacing source: use allChildren when
          // available so chip-filtered visible children don't change
          // which occurrence is surfaced.
          const surfacingSource: ParentRow =
            row.allChildren !== undefined
              ? { ...row, children: row.allChildren }
              : row;
          const childSurfacing = expanded
            ? deriveSurfacing(surfacingSource, now)
            : null;
          return (
            <Fragment key={`parent-${row.deliverable.id}`}>
              <ParentRowRender
                row={row}
                expanded={expanded}
                highlighted={highlightRowId === row.deliverable.id}
                onToggleExpand={onToggleExpand}
                onAction={onAction}
                now={now}
              />
              {expanded &&
                row.children.map((c) => {
                  const isSurfaced =
                    childSurfacing !== null &&
                    childSurfacing.surfacedOccurrenceId === c.deliverable.id;
                  const surfacedAsReason: "normal" | "overdue" | null =
                    isSurfaced
                      ? childSurfacing.surfacingReason === "overdue"
                        ? "overdue"
                        : "normal"
                      : null;
                  return (
                    <Fragment key={`child-${c.deliverable.id}`}>
                      <ChildRowRender
                        row={c}
                        highlighted={highlightRowId === c.deliverable.id}
                        ragFlashRowId={ragFlashRowId}
                        ragFlashRagRating={ragFlashRagRating}
                        onRowClick={onRowClick}
                        onAction={onAction}
                        milestonesById={milestonesById}
                        now={now}
                        surfacedAsReason={surfacedAsReason}
                        selected={expandedItemId === c.deliverable.id}
                      />
                      {inlineExpand(c.deliverable)}
                    </Fragment>
                  );
                })}
              {inlineExpand(row.deliverable)}
            </Fragment>
          );
        }
        if (row.kind === "one-off") {
          return (
            <Fragment key={`oneoff-${row.deliverable.id}`}>
              <OneOffRowRender
                row={row}
                highlighted={highlightRowId === row.deliverable.id}
                ragFlashRowId={ragFlashRowId}
                ragFlashRagRating={ragFlashRagRating}
                onRowClick={onRowClick}
                onAction={onAction}
                milestonesById={milestonesById}
                now={now}
                selected={expandedItemId === row.deliverable.id}
              />
              {inlineExpand(row.deliverable)}
            </Fragment>
          );
        }
        if (row.kind === "orphan") {
          return (
            <Fragment key={`orphan-${row.deliverable.id}`}>
              <OrphanRowRender
                row={row}
                highlighted={highlightRowId === row.deliverable.id}
                ragFlashRowId={ragFlashRowId}
                ragFlashRagRating={ragFlashRagRating}
                onRowClick={onRowClick}
                onAction={onAction}
                milestonesById={milestonesById}
                now={now}
                selected={expandedItemId === row.deliverable.id}
              />
              {inlineExpand(row.deliverable)}
            </Fragment>
          );
        }
        // Top-level ChildRows are not produced by groupDeliverables;
        // children only appear nested inside ParentRow above.
        return null;
      })}
    </>
  );
}

export default function DeliverableTable({
  rows,
  expandedScheduleIds,
  onToggleExpand,
  onRowClick,
  onAction,
  highlightRowId,
  ragFlashRowId,
  ragFlashRagRating,
  milestonesById,
  sortColumn,
  sortDirection,
  onSortChange,
  expandedItemId,
  renderExpandedRow,
}: DeliverableTableProps) {
  return (
    // Card container matches DeliverableGroupedView so the sequential
    // and grouped views read as the same contained surface — without
    // it, sequential rows float directly on the page background and
    // the recurring-parent fill looks like the ONLY filled row,
    // whereas in grouped every row sits on the card. Same wrapper =
    // consistent row treatment across both views.
    <div className="overflow-hidden rounded-lg border border-slate-200 bg-white text-slate-900 dark:border-slate-700 dark:bg-slate-800 dark:text-slate-100">
      <div className="overflow-auto">
        <table
          data-testid="deliverable-table"
          className="min-w-full border-collapse text-body-sm"
        >
          <thead className="sticky top-0 z-[5] bg-slate-50 dark:bg-slate-800">
            <DeliverableTableHeadRow
              sortColumn={sortColumn}
              sortDirection={sortDirection}
              onSortChange={onSortChange}
            />
          </thead>
          <tbody className="[&>tr:not(:first-child)]:border-t [&>tr:not(:first-child)]:border-t-slate-200 dark:[&>tr:not(:first-child)]:border-t-slate-700">
            <DeliverableTableBodyRows
              rows={rows}
              expandedScheduleIds={expandedScheduleIds}
              onToggleExpand={onToggleExpand}
              onRowClick={onRowClick}
              onAction={onAction}
              highlightRowId={highlightRowId}
              ragFlashRowId={ragFlashRowId}
              ragFlashRagRating={ragFlashRagRating}
              milestonesById={milestonesById}
              expandedItemId={expandedItemId}
              renderExpandedRow={renderExpandedRow}
            />
          </tbody>
        </table>
      </div>
    </div>
  );
}

